import { useSelector } from 'react-redux';
import { Selectors } from 'modules/Auth/AuthSlice';
import { useAbility } from 'services/Ability.service';

const CHAT_PROVIDER = import.meta.env.VITE_CHAT_PROVIDER || 'argide';
const ARGIDE_SCRIPT_ID = 'argide-widget-script';

// Argide helpers
// The script we load is only a loader: it fetches the product config and then
// injects the real widget bundle, which is what turns window.argide from the
// command queue into a function. So the loader's onload is far too early to
// call it — queue every command instead and let the bundle replay the queue.
function argideCommand(command, payload) {
  if (typeof window.argide === 'function') {
    window.argide(command, payload);
    return;
  }

  window.argide = window.argide || [];
  window.argide.push([command, payload]);
}

function identifyArgideUser(user) {
  if (!user?.id) return;

  argideCommand('update', {
    user_id: String(user.id),
    name: user.name,
    email: user.email,
    visibility: true,
  });
}

function loadArgideScript(user) {
  identifyArgideUser(user);

  if (document.getElementById(ARGIDE_SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.id = ARGIDE_SCRIPT_ID;
  script.src = 'https://dashboard.argide.ai/argide-b2b.iife.js';
  script.dataset.productId = import.meta.env.VITE_ARGIDE_PRODUCT_ID;
  script.dataset.apiUrl = import.meta.env.VITE_ARGIDE_API_URL;
  document.body.appendChild(script);
}

// An executed bundle can't be unloaded, so removing the loader's <script> tag
// leaves the mounted widget on screen. Tear the session down and hide it.
function resetArgideSession() {
  if (!document.getElementById(ARGIDE_SCRIPT_ID)) return;

  argideCommand('resetAndClearSession');
  argideCommand('update', { visibility: false });
}

// Intercom helpers
async function loadIntercom(user) {
  const { default: Intercom } = await import('@intercom/messenger-js-sdk');
  const appId = import.meta.env.VITE_INTERCOM_APP_ID;

  if (user?.id) {
    Intercom({
      app_id: appId,
      user_id: user.id,
      name: user.name,
      email: user.email,
      created_at: user.created_at,
    });
  } else {
    Intercom({ app_id: appId });
  }
}

async function shutdownIntercom() {
  const { shutdown } = await import('@intercom/messenger-js-sdk');
  shutdown();
}

export const useChatWidget = () => {
  const session = useSelector(Selectors.selectAuthData);
  const ability = useAbility();
  const showChat = ability.can('manage', 'intercom');

  const initChat = () => {
    if (!showChat) return;

    if (CHAT_PROVIDER === 'intercom') {
      loadIntercom(session?.user);
    } else {
      loadArgideScript(session?.user);
    }
  };

  // no permission guard here: logout clears permissions before this runs,
  // and the widget session still has to be torn down
  const hideChat = () => {
    if (CHAT_PROVIDER === 'intercom') {
      shutdownIntercom();
    } else {
      resetArgideSession();
    }
  };

  return {
    initChat,
    hideChat,
  };
};
